import { useState } from 'react';
import SEO from '@/components/SEO';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Newspaper, TrendingUp } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ContactPopup from '@/components/ContactPopup';
import { Button } from '@/components/ui/button';
import { newsArticles } from '@/data/mediaContent';

const NewsUpdates = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const getImportanceBadge = (importance) => {
    const colors = {
      High: 'bg-primary-100 text-primary-700 border-primary-600',
      Medium: 'bg-secondary-100 text-orange-700 border-orange-300',
      Low: 'bg-gray-100 text-gray-700 border-gray-300'
    };
    return colors[importance] || colors.Medium;
  };

  return (
    <>
      <SEO 
        title="News & Updates - Latest Trademark & IP Industry News"
        description="Stay updated with the latest trademark, copyright, and intellectual property news. Industry updates, legal changes, and company announcements from Trademark Protocols."
        keywords="IP news, trademark news, copyright updates, legal news, industry updates"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-12 md:py-16 overflow-hidden bg-primary-600">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(124,58,237,0.1),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(139,92,246,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="inline-block mb-4">
              <span className="bg-primary-600 text-white px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide">
                Latest Updates
              </span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              <span className="block">News & Updates</span>
              <span className="block mt-2 bg-primary-600 text-white">
                Stay Informed
              </span>
            </h1>
            
            <p className="text-lg text-gray-300 leading-relaxed">
              The latest industry news, legal updates, and company announcements from Trademark Protocols.
            </p>
          </div>
        </div>
      </section>

      {/* Featured News */}
      <section className="py-20 bg-transparent">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="w-6 h-6 text-primary-600" />
              <h2 className="text-3xl font-bold text-gray-900">Featured Update</h2>
            </div>
          </div>

          {newsArticles[0] && (
            <Link to={`/media/news/${newsArticles[0].slug}`} className="bg-white rounded-2xl overflow-hidden shadow-xl mb-16 block hover:shadow-2xl transition-shadow">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-80 md:h-auto">
                  <img
                    src={newsArticles[0].image}
                    alt={newsArticles[0].title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getImportanceBadge(newsArticles[0].importance)}`}>
                      {newsArticles[0].importance} Priority
                    </span>
                  </div>
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <div className="inline-block mb-4">
                    <span className="bg-primary-600 text-white px-4 py-1.5 rounded-full text-xs font-semibold">
                      {newsArticles[0].type}
                    </span>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    {newsArticles[0].title}
                  </h3>
                  <div className="flex items-center gap-2 text-gray-500 mb-4">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm">{newsArticles[0].date}</span>
                  </div>
                  <p className="text-lg text-gray-600 mb-6">
                    {newsArticles[0].excerpt}
                  </p>
                  <span className="text-primary-600 font-semibold flex items-center gap-2 group-hover:gap-3 transition-all">
                    Read Full Article
                    <ArrowRight className="w-5 h-5" />
                  </span>
                </div>
              </div>
            </Link>
          )}
        </div>
      </section>

      {/* All News */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <Newspaper className="w-6 h-6 text-primary-600" />
              <h2 className="text-3xl font-bold text-gray-900">Recent Updates</h2>
            </div>
          </div>

          <div className="space-y-8">
            {newsArticles.slice(1).map((item) => (
              <Link
                key={item.id}
                to={`/media/news/${item.slug}`}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 group block"
              >
                <div className="grid md:grid-cols-3 gap-0">
                  <div className="relative h-64 md:h-auto">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getImportanceBadge(item.importance)}`}>
                        {item.importance} Priority
                      </span>
                    </div>
                  </div>
                  <div className="md:col-span-2 p-8 flex flex-col justify-center">
                    <div className="inline-block mb-3">
                      <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                        {item.type}
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                      {item.title}
                    </h3>
                    <div className="flex items-center gap-2 text-gray-500 mb-3">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm">{item.date}</span>
                    </div>
                    <p className="text-gray-600 mb-4">
                      {item.excerpt}
                    </p>
                    <span className="text-primary-600 font-semibold flex items-center gap-2 group-hover:gap-3 transition-all">
                      Read More
                      <ArrowRight className="w-5 h-5" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-16 bg-primary-600">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Stay Updated with Our Newsletter
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Get the latest legal updates, industry news, and exclusive insights delivered to your inbox.
            </p>
           <div className="flex flex-wrap justify-center gap-4">
            <Button
              onClick={() => setIsPopupOpen(true)}
              className="bg-secondary-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-lg hover:border-white"
              data-testid="about-cta-btn"
            >
              Get Free Consultation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
          </div>
        </div>
      </section>

      <Footer />
      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </>
  );
};

export default NewsUpdates;